// Lo que un ticket significa en pantalla. Nada de esto se inventa acá: los tres
// enums salen de `data/models/operation/ticket.js`, y si el backend agrega un
// valor, las funciones de abajo lo muestran crudo en vez de esconderlo.

// ---------------------------------------------------------------------------
// TIPO — y el tipo NO decide nada.
//
// El modelo lo dice con todas las letras: «EL TIPO NO DECIDE EL FLUJO:
// `problema` y `sugerencia` son un dato. Nada crea un incidente a partir de un
// ticket.» Por eso los dos se dibujan con la MISMA tinta y se distinguen solo
// por la forma del icono. Pintar `problema` de rojo sería decir en color lo que
// el tier prohibió decir en código: que un problema reportado ya es un error
// confirmado. Eso lo dice la clasificación, más abajo, y solo después de que
// alguien lo mire.
export const TICKET_KINDS = [
    { key: 'problema',   label: 'Problema' },
    { key: 'sugerencia', label: 'Sugerencia' },
];

// ---------------------------------------------------------------------------
// ESTADO — el recorrido, en tres pasos.
//
// Los tonos son los que ya usan los estados de una tarea en
// `features/tasks/styles/overview.css`: gris lo que nadie miró todavía, el
// acento lo que está en manos de alguien, verde lo terminado. Es la misma
// escala porque es la misma pregunta —¿de esto se está ocupando alguien?— y dos
// escalas para lo mismo se desincronizan.
export const TICKET_STATES = [
    { key: 'recibido',    label: 'Recibido',    tone: 'idle' },
    { key: 'clasificado', label: 'Clasificado', tone: 'held' },
    { key: 'resuelto',    label: 'Resuelto',    tone: 'done' },
];

// ---------------------------------------------------------------------------
// CLASIFICACIÓN — el veredicto de quien lo miró.
//
// Acá sí hay color, y ahora está ganado: alguien puso su nombre y su fecha
// detrás de la palabra. `error` es lo único que afirma un defecto, así que es
// lo único en rojo.
export const TICKET_CLASSIFICATIONS = [
    { key: 'error',           label: 'Es un error',      tone: 'bad' },
    { key: 'no_reproducible', label: 'No reproducible',  tone: 'open' },
    { key: 'no_es_error',     label: 'No es un error',   tone: 'idle' },
    { key: 'mejora',          label: 'Mejora',           tone: 'held' },
    { key: 'duplicado',       label: 'Duplicado',        tone: 'idle' },
];

const BY_KEY = (list) => new Map(list.map(item => [item.key, item]));

const KIND_MAP = BY_KEY(TICKET_KINDS);
const STATE_MAP = BY_KEY(TICKET_STATES);
const CLASSIFICATION_MAP = BY_KEY(TICKET_CLASSIFICATIONS);

// Las tres devuelven `null` cuando el valor no está en la lista, y las pantallas
// muestran entonces la cadena cruda. El día que el backend agregue un cuarto
// estado, la fila dice el valor nuevo en vez de quedar vacía.
export const kindOf = (value) => KIND_MAP.get(value) ?? null;
export const stateOf = (value) => STATE_MAP.get(value) ?? null;
export const classificationOf = (value) => CLASSIFICATION_MAP.get(value) ?? null;

// ---------------------------------------------------------------------------
// CLASIFICADO O NO, calculado y no leído.
//
// El detalle (`GET /admin/tickets/{id}`) viene con `classified`; el listado
// (`GET /admin/tickets`) NO — las filas del listado salen de la misma vista SQL
// pero sin ese agregado. Así que la pantalla lo deriva del campo del que el
// backend lo deriva, y las dos respuestas dicen lo mismo.
//
// Y se pregunta por `classified_by` y no por el estado: reclasificar deja el
// estado donde estaba, así que un ticket `resuelto` también puede estar
// clasificado, y uno `clasificado` sin autor es imposible — la base lo prohíbe
// desde la migración 0032.
export function isClassified(ticket) {
    return ticket?.classified_by != null;
}

// QUIÉN REPORTÓ PUEDE NO SER UN USUARIO. `reported_by` es una persona de
// Mastermind; `reporter_ref` es a quién representa quien lo cargó, mientras no
// haya API pública. El alta exige uno de los dos, así que esto nunca debería
// devolver el último caso — y si lo devuelve, lo dice en vez de mostrar vacío.
export function reporterOf(ticket) {
    if (ticket?.reported_by_name) return { name: ticket.reported_by_name, known: true };
    if (ticket?.reporter_ref) return { name: ticket.reporter_ref, known: false };
    return null;
}

// EL ENTORNO Y LA VERSIÓN SON OPCIONALES Y NO SE COMPLETAN. Un ticket sin
// versión no toma la desplegada del entorno: eso sería afirmar qué versión
// tenía quien reportó. Cuando falta, la pantalla dice que falta.
export function affectedOf(ticket) {
    return {
        environment: ticket?.environment_kind ?? null,
        version: ticket?.version ?? null,
        // La vista del backend trae el repositorio del entorno, así que la
        // pantalla puede nombrarlo sin una consulta extra.
        repositoryId: ticket?.environment_repository_id ?? null,
    };
}

// LA FECHA VIENE EN SNAKE_CASE, y es la única lista del panel donde pasa.
//
// El resto de las rutas devuelven modelos de Sequelize, que serializan
// `createdAt`. Los tickets salen de una consulta SQL cruda (`SELECT k.*`), así
// que llegan con el nombre de la COLUMNA: `created_at`. Leer `createdAt` acá no
// falla ruidosamente — devuelve `undefined` y la fila queda sin fecha, que es
// la clase de error que nadie ve hasta que alguien pregunta por qué.
export function createdAtOf(ticket) {
    return ticket?.created_at ?? ticket?.createdAt ?? null;
}
